import {
  cacheKeys,
  DELETE_ROUTINE_DRAFT,
  FIND_ROUTINE_DRAFT,
  isDraftEnabledForRoutineType,
  useCustomMutation,
  useCustomQuery,
  useWorkspaceAsset,
} from '@gripp/shared-logic';
import {
  colors,
  Colors,
  EditRoutineModal,
  popNextAutoPrompt,
  RoutineButtons,
  RoutineDraftModal,
  RoutineExecutionProps,
  RoutineModal,
  SlideAction,
  SlideLayout,
  ViewMode,
} from '@gripp/shared-ui';
import { useQueryClient } from '@tanstack/react-query';
import { Icon } from 'libs/shared-ui/src/components/crop-circle/icons/Icon';
import { useCallback, useEffect, useState } from 'react';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import { useNavigate, useParams } from 'react-router-dom';
import { isFromActivitiesOrAssetPage } from '../assetUtils';
import { AssetCard } from './assetCard';
import { AssetFilters } from './assetFilters';

export const AssetRoutines = () => {
  const { assetId } = useParams();
  const navigate = useNavigate();

  if (!assetId) {
    return null;
  }

  const leftColumn = {
    title: 'assets.menu.title',
    mainComponent: <AssetCard assetId={assetId} />,
    headerComponent: <AssetFilters assetId={assetId} />,
    showBackButton: false,
    disableScroll: false,
  };

  const rightColumn = {
    title: 'asset.header.buttons.routines',
    mainComponent: <RightColumn assetId={assetId} />,
    headerComponent: <RoutinesHeaderActions assetId={assetId} />,
    showBackButton: true,
    onBackButtonPress: () => {
      try {
        const cameFromActivityOrAsset = isFromActivitiesOrAssetPage(document);

        if (cameFromActivityOrAsset && history?.state?.idx > 0) {
          navigate(-1);
          return;
        } else {
          navigate(`/assets/${assetId}`);
        }
      } catch (e) {
        navigate(`/assets/${assetId}`);
      }
    },
    backButtonTransition: SlideAction.PREV,
  };

  return (
    <SlideLayout
      leftColumn={leftColumn}
      rightColumn={rightColumn}
      viewMode={ViewMode.DUAL}
    />
  );
};

const RoutinesHeaderActions = ({ assetId }: { assetId: string }) => {
  const [editModalVisible, setEditModalVisible] = useState(false);
  const { workspaceAsset } = useWorkspaceAsset(assetId);

  return (
    <View style={styles.headerActions}>
      <Pressable onPress={() => setEditModalVisible(true)}>
        <Icon name="Settings" size={24} />
      </Pressable>
      {workspaceAsset && (
        <EditRoutineModal
          workspaceAsset={workspaceAsset}
          editRoutineModalVisible={editModalVisible}
          onClose={() => setEditModalVisible(false)}
          modalStyle={styles.editModalStyle}
          isWeb={Platform.OS === 'web'}
        />
      )}
    </View>
  );
};

const RightColumn = ({ assetId }: { assetId: string }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { workspaceAsset, invalidateQuery: invalidateWorkspaceAsset } =
    useWorkspaceAsset(assetId);

  const [executeModalVisible, setExecuteModalVisible] = useState(false);
  const [executeModalProps, setExecuteModalProps] = useState<
    RoutineExecutionProps | undefined
  >();
  const [showDraftModal, setShowDraftModal] = useState(false);
  const [loadedDraftData, setLoadedDraftData] = useState<any>(null);
  const [formKey, setFormKey] = useState(0);
  const [justSavedDraft, setJustSavedDraft] = useState(false);

  const showSaveDraftButton = isDraftEnabledForRoutineType(
    executeModalProps?.routine?.type
  );

  const { data: draftQueryData, isLoading: isDraftLoading } = useCustomQuery({
    cacheKey: [
      'routineDraft',
      workspaceAsset?.id,
      executeModalProps?.routine?.id,
    ],
    query: FIND_ROUTINE_DRAFT,
    variables: {
      workspaceAssetId: workspaceAsset?.id || '',
      routineId: executeModalProps?.routine?.id || '',
    },
    options: {
      enabled: !!(
        workspaceAsset?.id &&
        executeModalProps?.routine?.id &&
        executeModalVisible &&
        showSaveDraftButton
      ),
    },
  });

  const draft = draftQueryData?.routine?.findRoutineDraft;

  const { mutateAsync: deleteDraftAsync } = useCustomMutation({
    query: DELETE_ROUTINE_DRAFT,
  });

  useEffect(() => {
    if (!isDraftLoading && executeModalProps && executeModalVisible) {
      if (draft && !justSavedDraft) {
        setShowDraftModal(true);
      } else {
        setLoadedDraftData(null);
      }
    }
  }, [
    draft,
    isDraftLoading,
    justSavedDraft,
    executeModalProps,
    executeModalVisible,
  ]);

  const invalidateIssues = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: cacheKeys('issue').all });
  }, [queryClient]);

  const onRoutineSelected = (selectedProps: RoutineExecutionProps) => {
    setExecuteModalProps(selectedProps);
    setJustSavedDraft(false);
    setExecuteModalVisible(true);
  };

  const handleSaveDraft = async () => {
    setJustSavedDraft(true);
    await queryClient.invalidateQueries({
      queryKey: [
        'routineDraft',
        workspaceAsset?.id,
        executeModalProps?.routine?.id,
      ],
    });
  };

  const handleContinueDraft = async () => {
    setShowDraftModal(false);
    setLoadedDraftData(draft?.data || null);
    setFormKey((prev) => prev + 1);
  };

  const handleStartNew = async () => {
    setShowDraftModal(false);
    try {
      if (workspaceAsset?.id && executeModalProps?.routine?.id) {
        await deleteDraftAsync({
          input: {
            workspaceAssetId: workspaceAsset.id,
            routineId: executeModalProps.routine.id,
          },
        });
      }
      setLoadedDraftData(null);
      await queryClient.invalidateQueries({
        queryKey: [
          'routineDraft',
          workspaceAsset?.id,
          executeModalProps?.routine?.id,
        ],
      });
      await new Promise((resolve) => setTimeout(resolve, 100));
      setFormKey((prev) => prev + 1);
    } catch (error) {
      console.error('Error deleting draft:', error);
    }
  };

  const handleDraftCancel = () => {
    setShowDraftModal(false);
    setExecuteModalVisible(false);
    setExecuteModalProps(undefined);
  };

  const handleRoutineCompleted = async () => {
    try {
      if (workspaceAsset?.id && executeModalProps?.routine?.id) {
        await deleteDraftAsync({
          input: {
            workspaceAssetId: workspaceAsset.id,
            routineId: executeModalProps.routine.id,
          },
        });
        await queryClient.invalidateQueries({
          queryKey: [
            'routineDraft',
            workspaceAsset?.id,
            executeModalProps?.routine?.id,
          ],
        });
      }
    } catch (error) {
      console.error('Error deleting draft after completion:', error);
    }

    const nextRoutine = executeModalProps
      ? popNextAutoPrompt(executeModalProps)
      : null;
    if (nextRoutine) {
      setExecuteModalProps(nextRoutine);
      setJustSavedDraft(false);
      setLoadedDraftData(null);
      setFormKey((prev) => prev + 1);
      return;
    }

    setExecuteModalVisible(false);
    await invalidateWorkspaceAsset();
    await invalidateIssues();
  };

  if (!workspaceAsset) return null;

  return (
    <View style={styles.container}>
      <RoutineButtons
        workspaceAsset={workspaceAsset}
        onRoutineSelected={onRoutineSelected}
        hideAssignRoutinesButton={true}
        showAll={true}
        initialRoutinesToShow={100}
      />
      {executeModalProps && (
        <RoutineModal
          key={formKey}
          {...executeModalProps}
          visible={executeModalVisible}
          confirmCancel={true}
          onCanceled={() => {
            setExecuteModalVisible(false);
          }}
          onCompleted={handleRoutineCompleted}
          onSaveDraft={handleSaveDraft}
          alertsRightButton={true}
          showSaveDraftButton={showSaveDraftButton}
          draftData={loadedDraftData}
          navigateToAlerts={navigate}
          customModalDraft={
            showDraftModal ? (
              <RoutineDraftModal
                visible={showDraftModal}
                onClose={handleDraftCancel}
                onContinueDraft={handleContinueDraft}
                onStartNew={handleStartNew}
                draft={draft}
                workspaceAsset={workspaceAsset}
                routine={executeModalProps?.routine}
                showPortal={false}
              />
            ) : null
          }
          showNativeModal={true}
          showConfirmBack={true}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.brand.fullWhite,
    padding: 16,
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  editModalStyle: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    paddingTop: 40,
    backgroundColor: Colors.blackInactive,
  },
});
